import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Empty } from "@/components/Shell";
import { Field } from "@/lib/ui";
import ExamTaker from "@/components/ExamTaker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Cross, CheckCircle2, KeyRound } from "lucide-react";

export default function EntrancePortal() {
  const { L } = useLang();
  const navigate = useNavigate();
  const [cred, setCred] = useState({ name: "", pin: "" });
  const [session, setSession] = useState(null); // {candidate_id, name, grade_level, exams}
  const [exam, setExam] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const login = async () => {
    if (!cred.name.trim() || !cred.pin.trim()) { toast.error(L("Enter your name and PIN", "Entrez votre nom et votre PIN")); return; }
    try {
      const r = await api.post("/entrance/login", { name: cred.name.trim(), pin: cred.pin.trim() });
      setSession(r);
      if ((r.exams || []).length === 0) toast.info(L("No entrance exam is available for your class yet.", "Aucun examen d'entrée n'est disponible pour votre classe."));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const start = async (e) => {
    try { const full = await api.get(`/entrance/exams/${e.id}`, { params: { pin: cred.pin.trim(), candidate_id: session.candidate_id } }); setExam(full); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };
  const submit = async (answers) => {
    setSubmitting(true);
    try { const r = await api.post(`/entrance/exams/${exam.id}/submit`, { candidate_id: session.candidate_id, pin: cred.pin.trim(), answers }); setDone(r); setExam(null); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-primary text-primary-foreground">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-accent flex items-center justify-center"><Cross className="h-5 w-5 text-primary" /></div>
            <div><p className="font-heading font-extrabold text-sm">ICDH · {L("Entrance Exam", "Examen d'entrée")}</p><p className="text-[11px] text-primary-foreground/60">Institution Chrétienne D'Haïti</p></div>
          </div>
          <Button size="sm" variant="ghost" className="text-primary-foreground hover:bg-white/10" onClick={() => navigate("/")}>{L("Exit", "Quitter")}</Button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto p-4 sm:p-8">
        {!session && (
          <div className="max-w-md mx-auto animate-fade-up">
            <h1 className="font-heading font-extrabold text-2xl tracking-tight mb-1">{L("Entrance Exam Portal", "Portail Examen d'entrée")}</h1>
            <p className="text-muted-foreground mb-6">{L("Enter your full name and the Testing PIN the school gave you.", "Entrez votre nom complet et le PIN de test remis par l'école.")}</p>
            <div className="space-y-4 bg-card border border-border rounded-lg p-6">
              <Field label={L("Full name", "Nom complet")}><Input value={cred.name} onChange={(e) => setCred({ ...cred, name: e.target.value })} data-testid="entrance-name" placeholder="Jean Pierre" /></Field>
              <Field label={L("Testing PIN", "PIN de test")}>
                <div className="relative">
                  <KeyRound className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input className="pl-9 tracking-widest" value={cred.pin} onChange={(e) => setCred({ ...cred, pin: e.target.value })} data-testid="entrance-pin" placeholder="123456" />
                </div>
              </Field>
              <Button className="w-full" onClick={login} data-testid="entrance-login-btn">{L("Continue", "Continuer")}</Button>
            </div>
          </div>
        )}

        {session && !exam && !done && (
          <div className="animate-fade-up">
            <button className="text-sm text-primary mb-4" onClick={() => { setSession(null); setCred({ name: "", pin: "" }); }}>← {L("Back", "Retour")}</button>
            <h1 className="font-heading font-extrabold text-2xl mb-1">{L("Welcome", "Bienvenue")}, {session.name}</h1>
            <p className="text-muted-foreground mb-4">{L("Class", "Classe")}: {session.grade_level}</p>
            {(session.exams || []).length === 0 ? <Empty title={L("No exam available", "Aucun examen disponible")} hint={L("Please check with the school administration.", "Veuillez vérifier auprès de l'administration.")} /> : (
              <div className="grid sm:grid-cols-2 gap-4">
                {session.exams.map((e) => (
                  <div key={e.id} className="bg-card border border-border rounded-lg p-5" data-testid={`entrance-exam-${e.id}`}>
                    <p className="font-heading font-bold text-lg">{e.title}</p>
                    <p className="text-sm text-muted-foreground">{e.subject} · {e.question_count} {L("questions", "questions")}{e.duration_min ? ` · ${e.duration_min} min` : ""}</p>
                    <Button className="mt-4" onClick={() => start(e)} data-testid={`start-entrance-${e.id}`}>{L("Start", "Commencer")}</Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {exam && (
          <div className="animate-fade-up">
            <h1 className="font-heading font-extrabold text-2xl mb-1">{exam.title}</h1>
            <p className="text-muted-foreground mb-6">{exam.subject} · {session.name}</p>
            <ExamTaker exam={exam} onSubmit={submit} submitting={submitting} L={(en, fr) => (L ? L(en, fr) : en)} />
          </div>
        )}

        {done && (
          <div className="max-w-md mx-auto text-center animate-fade-up bg-card border border-border rounded-lg p-8" data-testid="entrance-done">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
            <h1 className="font-heading font-extrabold text-2xl mt-4">{L("Thank you!", "Merci !")}</h1>
            <p className="text-muted-foreground mt-2">{L("Your entrance exam has been submitted. The administration will review your results.", "Votre examen d'entrée a été soumis. L'administration examinera vos résultats.")}</p>
            <Button className="mt-6" onClick={() => navigate("/")}>{L("Done", "Terminé")}</Button>
          </div>
        )}
      </main>
    </div>
  );
}
